/**
 * Copyright 2024-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { SubmitHandler } from "react-hook-form";
import { v4 } from "uuid";
import { Button, FlexContainer, Message, Modal, theme } from "@nordcloud/gnui";
import { KeyValue } from "~/generated/graphql";
import { CreateResourceGroupDetails } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/CreateResourceGroupDetails";
import {
  CreateResourceGroup,
  FormField,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/types";

type FormData = {
  name: string;
  resourceIds?: string[];
  description?: string;
  metadata?: KeyValue[];
};

type Props = {
  onApply: (newResourceGroup: CreateResourceGroup) => void;
  isOpen: boolean;
  onCancel: () => void;
  defaultValues?: CreateResourceGroup;
};

export function CreateResourceGroupModal({
  onApply,
  onCancel,
  isOpen,
  defaultValues,
}: Props) {
  const onSubmit: SubmitHandler<FormData> = (data: FormData) => {
    const newResourceGroup: CreateResourceGroup = {
      [FormField.LIST_ID]: defaultValues?.listId ?? v4(),
      [FormField.NAME]: data.name,
      [FormField.DESCRIPTION]: data.description,
      [FormField.METADATA]: data.metadata,
      [FormField.RESOURCE_IDS]: defaultValues?.resourceIds,
    };
    onApply(newResourceGroup);
  };

  return (
    <Modal
      onClose={onCancel}
      isOpen={isOpen}
      contentLabel="Create Resource Group"
      contentMaxHeight="80vh"
    >
      <>
        <CreateResourceGroupDetails
          initialValues={{
            details: {
              name: defaultValues?.name ?? "",
              description: defaultValues?.description ?? "",
              metadata: defaultValues?.metadata ?? [],
            },
          }}
          onSubmit={onSubmit}
          formId="createResourceGroupForm"
        />
        <div
          css={{
            marginTop: theme.spacing.spacing04,
            marginBottom: theme.spacing.spacing04,
          }}
        >
          <Message status="notification" image="info">
            Resource group will be created after the plan is finalized.
          </Message>
        </div>
        <FlexContainer justifyContent="end" alignItems="center">
          <Button
            mr={theme.spacing.spacing04}
            severity="low"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="createResourceGroupForm"
            mr={theme.spacing.spacing04}
            severity="high"
          >
            Apply
          </Button>
        </FlexContainer>
      </>
    </Modal>
  );
}
